import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({ 
  title = "Francis Odame - Author, Health Advocate & Community Voice",
  description = "Francis Odame is a passionate author, health advocate, and community voice dedicated to inspiring healthier, purpose-driven lives through powerful storytelling and wellness advocacy.",
  keywords = "Francis Odame, author, health advocate, community voice, Because of Abigail, speaking, wellness, mental health, community outreach, book, inspirational",
  image = "/assets/logo2.png",
  url = "https://francisodame.com",
  type = "website",
  author = "Francis Odame",
  publishedTime,
  modifiedTime,
  section = "Health & Wellness",
  tags = ["Health", "Wellness", "Community", "Mental Health", "Inspiration"],
  bookTitle = "Because of Abigail",
  bookDescription = "A heartfelt story of love and loss that inspires resilience and hope. This powerful narrative explores themes of healing, growth, and the transformative power of relationships.",
  isbn = "978-1834187440",
  bookAuthor = "Francis Odame",
  bookPublisher = "Francis Odame Publishing",
  bookPublishedDate = "2024-01-01",
  bookGenre = "Inspirational Fiction"
}) => {
  const fullTitle = title.includes("Francis Odame") ? title : `${title} | Francis Odame`;
  const fullUrl = url.startsWith('http') ? url : `https://francisodame.com${url}`;
  const fullImage = image.startsWith('http') ? image : `https://francisodame.com${image}`;

  // Structured Data for Author
  const authorStructuredData = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Francis Odame",
    "jobTitle": "Author, Health Advocate, Community Voice",
    "description": "A passionate writer and dedicated healthcare professional whose mission is to inspire, educate, and empower people through words and wellness.",
    "url": "https://francisodame.com",
    "image": "https://francisodame.com/assets/logo2.png",
    "sameAs": [
      "https://www.facebook.com/share/1AS7TuWt2U/?mibextid=wwXIfr",
      "https://www.instagram.com/fodamewrites?igsh=MWF5OHQ4eDlzeHZ2aw%3D%3D&utm_source=qr",
      "https://www.linkedin.com/in/francis-odame-2833a7216?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=ios_app",
      "https://www.tiktok.com/@fodamewrites?_t=ZN-907HCZMxm6w&_r=1"
    ],
    "knowsAbout": [
      "Health Advocacy",
      "Community Outreach",
      "Mental Health Awareness",
      "Youth Empowerment",
      "Faith-based Wellness",
      "Public Speaking",
      "Writing",
      "Storytelling"
    ],
    "alumniOf": "Healthcare Professional",
    "worksFor": {
      "@type": "Organization",
      "name": "Francis Odame Writes"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+44-7380-242303",
      "contactType": "customer service",
      "availableLanguage": "English"
    }
  };

  // Structured Data for Book
  const bookStructuredData = {
    "@context": "https://schema.org",
    "@type": "Book",
    "name": bookTitle,
    "author": {
      "@type": "Person",
      "name": bookAuthor
    },
    "publisher": {
      "@type": "Organization",
      "name": bookPublisher
    },
    "datePublished": bookPublishedDate,
    "description": bookDescription,
    "genre": bookGenre,
    "isbn": isbn,
    "image": "https://francisodame.com/assets/newCover.png",
    "offers": [
      {
        "@type": "Offer",
        "url": "https://amzn.eu/d/7zSqF9E",
        "priceCurrency": "GBP",
        "availability": "https://schema.org/InStock",
        "seller": {
          "@type": "Organization",
          "name": "Amazon"
        }
      },
      {
        "@type": "Offer",
        "url": "https://www.waterstones.com/book/because-of-abigail/francis-odame/9781834187440",
        "priceCurrency": "GBP",
        "availability": "https://schema.org/InStock",
        "seller": {
          "@type": "Organization",
          "name": "Waterstones"
        }
      }
    ]
  };

  // Structured Data for Website
  const websiteStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Francis Odame Writes",
    "url": "https://francisodame.com",
    "description": "Official website of Francis Odame - Author, Health Advocate, and Community Voice. Discover his books, speaking services, and wellness advocacy work.",
    "author": {
      "@type": "Person",
      "name": "Francis Odame"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Francis Odame Writes"
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://francisodame.com/search?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      <meta name="bingbot" content="index, follow" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />
      
      {/* Language */}
      <meta httpEquiv="content-language" content="en-GB" />
      <meta name="language" content="English" />
      
      {/* Geo Tags */}
      <meta name="geo.region" content="GB" />
      <meta name="geo.country" content="United Kingdom" />
      
      {/* Open Graph Meta Tags */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:site_name" content="Francis Odame Writes" />
      <meta property="og:locale" content="en_GB" />
      
      {/* Open Graph Article Tags (for blog posts) */}
      {type === "article" && (
        <>
          <meta property="article:author" content={author} />
          <meta property="article:section" content={section} />
          {publishedTime && <meta property="article:published_time" content={publishedTime} />}
          {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
          {tags.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* Twitter Card Meta Tags - Removed */}
      
      {/* Additional Meta Tags */}
      <meta name="theme-color" content="#00A896" />
      <meta name="msapplication-TileColor" content="#00A896" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
      
      {/* Preconnect to external domains */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      
      {/* Favicon */}
      <link rel="icon" type="image/png" sizes="32x32" href="/assets/logo2.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/assets/logo2.png" />
      <link rel="apple-touch-icon" href="/assets/logo2.png" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(authorStructuredData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(bookStructuredData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteStructuredData)}
      </script>
    </Helmet>
  );
};

export default SEO;
